import { useState } from "react";
import { Search, Check } from "lucide-react";
import CountryFlag from "../transfer-windows/CountryFlag";
import AttachedDocLink from "../common/AttachedDocLink";
import RequestPreviewModal from "../common/RequestPreviewModal";
import useTranslate from "../../hooks/useTranslate";
import {
  formatSalary as formatSalaryShared,
  getSalaryKind,
  formatDivisionLine as formatDivisionLineShared,
} from "../../utils/tenderFormatters";

// D-011 lawyer view of a STAFF request. Deliberately slim: no save / boost /
// pin / delete (those are agent/club actions) — a lawyer only reads the request
// and applies. Shares the tender formatters + loupe preview with the staff/agent
// cards so the numbers never diverge.
const LawyerTenderCard = ({ tender, applied = false, onApply }) => {
  const { t } = useTranslate();
  const [showPreview, setShowPreview] = useState(false);

  if (!tender) return null;

  const formatPosition = (position) =>
    !position
      ? ""
      : position
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

  const salary = formatSalaryShared(tender) || "";
  const salaryKind = getSalaryKind(tender) || "Net";
  const divisionLine = formatDivisionLineShared(tender);
  const languages =
    tender.language_skills && tender.language_skills.length > 0
      ? tender.language_skills.join(", ")
      : "-";

  const applyButton = applied ? (
    <div className="w-full py-2 rounded-lg bg-[#E7F8EF] text-[#12B76A] text-sm font-medium flex items-center justify-center gap-2">
      <Check size={16} />
      {t("Applied")}
    </div>
  ) : (
    <button
      onClick={() => onApply?.(tender)}
      className="w-full py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
    >
      {t("Apply")}
    </button>
  );

  return (
    <div className="bg-white rounded-lg p-6 border border-[#D2D4D880] flex flex-col h-full">
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-col gap-2 min-w-0">
          <h3 className="text-lg font-semibold">
            {formatPosition(tender.position)}
          </h3>
          {tender.club && (
            <div className="flex items-center gap-2">
              <img
                src={tender.club.logo}
                alt={tender.club.name}
                className="w-8 h-8 object-contain"
                referrerPolicy="no-referrer"
              />
              <span className="text-[18px] text-[#242426] font-medium truncate">
                {tender.club.name}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <AttachedDocLink tender={tender} size={5} />
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label={t("Preview request")}
            title={t("Preview request")}
          >
            <Search className="w-5 h-5 text-[#535370]" />
          </button>
        </div>
      </div>

      {divisionLine && (
        <div className="flex items-center gap-2 mb-4">
          <p className="text-[#242426] text-sm font-medium">{divisionLine}</p>
          <CountryFlag
            country={tender.division_country?.name}
            imageUrl={tender.division_country?.image_url}
            width={20}
            height={14}
          />
        </div>
      )}

      <p className="text-sm text-[#535370] mb-4 overflow-hidden text-ellipsis line-clamp-2">
        {tender.description || t("No description available")}
      </p>

      <div className="flex justify-between items-center mb-4 bg-[#F7F7F7] p-2 rounded-lg">
        <div className="w-full flex justify-between items-center">
          <p className="text-sm font-medium text-[#1A212F]">
            {/* Monthly, like every other reading of this number — see
                StaffTenderCard. "Monthly salary" is already a registered
                string (translations/pages/agent/agent-tenders.json), and all
                bundles merge into one shared namespace, so the 16 languages
                come along. */}
            {salaryKind} {t("Monthly salary")}
          </p>
          <p className="font-medium text-[#000000]">{salary}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 mb-6">
        <div className="flex justify-between items-center">
          <p className="text-[12px] text-[#1A212F]">{t("Request Type")}</p>
          <p
            className={`px-3 py-1 rounded-full text-sm ${
              tender.type_of_tender?.toLowerCase() === "full_time"
                ? "text-white bg-[#049669]"
                : tender.type_of_tender?.toLowerCase() === "contract"
                ? "bg-blue-100 text-blue-600"
                : "bg-orange-100 text-orange-600"
            }`}
          >
            {tender.type_of_tender
              ? tender.type_of_tender.charAt(0).toUpperCase() +
                tender.type_of_tender.slice(1)
              : "N/A"}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-[12px] text-[#1A212F]">{t("Experience")}</p>
          <p className="text-[#242426]">
            {tender?.experience} {t("years")}
          </p>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-[12px] text-[#1A212F]">{t("Languages known")}</p>
          <p className="text-[#242426]">{languages}</p>
        </div>
      </div>

      <div className="mt-auto">{applyButton}</div>

      <RequestPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        tender={tender}
        primaryAction={
          applied ? (
            <div className="w-full py-2 rounded-lg bg-[#E7F8EF] text-[#12B76A] text-sm font-medium flex items-center justify-center gap-2">
              <Check size={16} />
              {t("Applied")}
            </div>
          ) : (
            <button
              onClick={() => {
                setShowPreview(false);
                onApply?.(tender);
              }}
              className="w-full py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
            >
              {t("Apply")}
            </button>
          )
        }
      />
    </div>
  );
};

export default LawyerTenderCard;
